import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';
import { Building2, CalendarDays, ClipboardList, Star, ArrowRight, Plus, Mail, Handshake, Users, Eye, Ticket, Crown, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUser } from '@/lib/auth';
import SocialPostModal from '@/components/SocialPostModal';

export default function AdminDashboard() {
  const user = useUser();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [pending, setPending] = useState([]);
  const [locali, setLocali] = useState([]);
  const [socialEvent, setSocialEvent] = useState(null);

  const can = (p) => user?.is_super_admin || !!user?.permissions?.[p];

  useEffect(() => {
    if (can('can_view_stats')) {
      api.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
    }
    if (can('can_create_events') || can('can_publish_events')) {
      // Latest 20 PUBLISHED events, sorted by most-recent creation (proxy for publish time)
      api.get('/admin/eventi', { params: { stato: 'Pubblicato' } })
        .then((r) => {
          const list = (r.data || []).slice().sort((a, b) => {
            const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
            return tb - ta;
          });
          setRecent(list.slice(0, 20));
        })
        .catch(() => setRecent([]));
      api.get('/admin/locali').then((r) => setLocali(r.data || [])).catch(() => {});
      if (can('can_publish_events')) {
        api.get('/admin/eventi', { params: { stato: 'In Revisione' } }).then((r) => setPending((r.data || []).slice(0, 5)));
      }
    }
    // eslint-disable-next-line
  }, [user?.id]);

  const localeMap = Object.fromEntries(locali.map((l) => [l.id, l]));

  const cards = [
    { label: 'Eventi totali', value: stats?.total_eventi ?? '—', icon: CalendarDays, to: '/admin/eventi', show: can('can_view_stats') },
    { label: 'In Revisione', value: stats?.in_revisione ?? '—', icon: ClipboardList, to: '/admin/revisione', highlight: stats?.in_revisione > 0, show: can('can_view_stats') },
    { label: 'Pubblicati', value: stats?.pubblicati ?? '—', icon: CalendarDays, to: '/admin/eventi', show: can('can_view_stats') },
    { label: 'Featured', value: stats?.in_evidenza ?? '—', icon: Star, to: '/admin/eventi', show: can('can_view_stats') },
    { label: 'Locali', value: stats?.total_locali ?? '—', icon: Building2, to: '/admin/locali', show: can('can_view_stats') },
    { label: 'Newsletter', value: stats?.total_newsletter ?? '—', icon: Mail, to: '/admin/newsletter', show: can('can_manage_newsletter') && can('can_view_stats') },
    { label: 'Collab. nuove', value: stats?.collaborazioni_da_contattare ?? '—', icon: Handshake, to: '/admin/collaborazioni', show: can('can_view_stats'), highlight: stats?.collaborazioni_da_contattare > 0 },
    { label: 'Visualizzazioni', value: stats?.total_views ?? '—', icon: Eye, to: '/admin/eventi', show: can('can_view_stats') },
    { label: 'Click Acquista', value: stats?.total_buy_clicks ?? '—', icon: Ticket, to: '/admin/eventi', show: can('can_view_stats') },
    { label: 'Click Tavolo', value: stats?.total_table_clicks ?? '—', icon: Crown, to: '/admin/eventi', show: can('can_view_stats') },
  ].filter(c => c.show);

  return (
    <AdminLayout title={`Ciao, ${user?.nome || user?.email || 'Admin'}`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <p className="text-sm text-[color:var(--text-muted)]">Panoramica del portale e azioni rapide</p>
        <div className="flex gap-2 flex-wrap">
          {can('can_create_events') && <Link to="/admin/eventi/nuovo"><Button data-testid="dashboard-new-event" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold"><Plus className="w-4 h-4 mr-1"/>Nuovo Evento</Button></Link>}
          {can('can_manage_locali') && <Link to="/admin/locali"><Button variant="outline" data-testid="dashboard-new-locale" className="border-[color:var(--gold)] text-[color:var(--gold)]"><Plus className="w-4 h-4 mr-1"/>Nuovo Locale</Button></Link>}
          {user?.is_super_admin && <Link to="/admin/team"><Button variant="ghost" data-testid="dashboard-team"><Users className="w-4 h-4 mr-1"/>Team</Button></Link>}
        </div>
      </div>

      {cards.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-8">
          {cards.map((c) => (
            <Link key={c.label} to={c.to} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,'-').replace(/\./g,'')}`} className={`rounded-xl border p-4 transition-colors hover:border-[color:var(--gold)] ${c.highlight ? 'border-[color:var(--gold)] bg-[rgba(214,185,140,0.06)]' : 'border-[color:var(--border-c)] bg-[color:var(--surface)]'}`}>
              <div className="flex items-center justify-between mb-2">
                <c.icon className="w-4 h-4 text-[color:var(--gold)]" />
                <ArrowRight className="w-3 h-3 text-[color:var(--text-subtle)]"/>
              </div>
              <div className="text-3xl font-heading">{c.value}</div>
              <div className="text-xs text-[color:var(--text-muted)] uppercase tracking-wider mt-1">{c.label}</div>
            </Link>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {can('can_publish_events') && (
          <Panel title="Coda Revisione" linkTo="/admin/revisione" linkText="Apri coda">
            {pending.length === 0 ? <Empty text="Nessun evento in revisione"/> : pending.map((e) => (
              <RowItem key={e.id} title={e.titolo} subtitle={`${e.data} • ${e.zona}`} to={`/admin/eventi/${e.id}/modifica`} />
            ))}
          </Panel>
        )}
        {(can('can_create_events') || can('can_publish_events')) && (
          <Panel title="Ultimi 20 eventi pubblicati" linkTo="/admin/eventi" linkText="Vedi tutti">
            <div data-testid="admin-dashboard-recent-list" data-count={recent.length}>
              {recent.length === 0 ? <Empty text="Nessun evento pubblicato ancora"/> : recent.map((e) => (
              <RowItem
                key={e.id}
                title={e.titolo}
                subtitle={`${e.data} • ${e.stato_revisione}`}
                to={`/admin/eventi/${e.id}/modifica`}
                action={
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`social-post-btn-${e.id}`}
                    aria-label="Social Post"
                    title="Genera post per Instagram"
                    onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setSocialEvent(e); }}
                    className="text-[color:var(--gold)] hover:bg-[rgba(214,185,140,0.08)] shrink-0"
                  >
                    <Instagram className="w-4 h-4 mr-1" />
                    Social Post
                  </Button>
                }
              />
            ))}
            </div>
          </Panel>
        )}
        {!can('can_view_stats') && !can('can_create_events') && !can('can_publish_events') && !can('can_manage_locali') && (
          <div className="col-span-full text-center py-12 text-[color:var(--text-muted)]">Nessuna sezione disponibile con i tuoi permessi. Contatta il Super Admin.</div>
        )}
      </div>

      {/* TOP EVENTS BY CLICK */}
      {can('can_view_stats') && stats?.top_events_by_buy?.length > 0 && (
        <div className="mt-6 rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)]" data-testid="top-events-by-buy">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-c)]">
            <h3 className="font-heading text-lg flex items-center gap-2"><Ticket className="w-4 h-4 text-[color:var(--gold)]"/>Top eventi per click "Acquista"</h3>
            <span className="text-xs text-[color:var(--text-subtle)]">Aggiornato in tempo reale</span>
          </div>
          <div className="divide-y divide-[color:var(--border-c)]">
            {stats.top_events_by_buy.map((ev) => (
              <Link key={ev.id} to={`/admin/eventi/${ev.id}/modifica`} className="flex items-center justify-between px-4 py-3 hover:bg-[rgba(214,185,140,0.04)]">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{ev.titolo}</div>
                  <div className="text-xs text-[color:var(--text-muted)] flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-[color:var(--gold)]"/>{ev.view_count || 0}</span>
                    <span className="flex items-center gap-1 text-emerald-300"><Ticket className="w-3 h-3"/>{ev.click_buy_count || 0}</span>
                    <span className="flex items-center gap-1 text-amber-300"><Crown className="w-3 h-3"/>{ev.click_table_count || 0}</span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[color:var(--text-subtle)]"/>
              </Link>
            ))}
          </div>
        </div>
      )}
      {/* SOCIAL POST MODAL */}
      <SocialPostModal
        open={!!socialEvent}
        onOpenChange={(o) => { if (!o) setSocialEvent(null); }}
        evento={socialEvent}
        locale={socialEvent ? localeMap[socialEvent.locale_id] : null}
      />
    </AdminLayout>
  );
}

function Panel({ title, linkTo, linkText, children }) {
  return (
    <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-c)]">
        <h3 className="font-heading text-lg">{title}</h3>
        <Link to={linkTo} className="text-xs text-[color:var(--gold)] hover:text-[color:var(--gold-soft)]">{linkText} →</Link>
      </div>
      <div className="divide-y divide-[color:var(--border-c)]">{children}</div>
    </div>
  );
}
function RowItem({ title, subtitle, to, action }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 hover:bg-[rgba(214,185,140,0.04)]">
      <Link to={to} className="flex items-center justify-between flex-1 min-w-0">
        <div className="min-w-0">
          <div className="font-medium truncate">{title}</div>
          <div className="text-xs text-[color:var(--text-muted)]">{subtitle}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-[color:var(--text-subtle)] shrink-0 ml-2"/>
      </Link>
      {action}
    </div>
  );
}
function Empty({ text }) {
  return <div className="px-4 py-6 text-sm text-[color:var(--text-muted)]">{text}</div>;
}
